/**
 * One slim line about the agent's read of a document, for editors, and only
 * when it asks something of them: the agent has never read it, it is reading
 * now, or a fresh read disagrees with what a person saved (the count plus
 * Accept All, which first lists what it will fill in). The proposals
 * themselves sit inline under their fields (`EntryFieldsForm`). A failed
 * read is a callout; the model, time and transcript are staff-only.
 */
import { Check, Loader2, Sparkles } from "lucide-react";
import { useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";

import type { FirmLibrarySuggestion } from "../types";

type Props = {
  suggestion: FirmLibrarySuggestion | null | undefined;
  /** Proposals that differ from the form and were not dismissed. */
  pendingCount: number;
  /** What Accept All would put in the form, shown before it does. */
  changes: { field: string; label: string; value: string }[];
  busy: boolean;
  onAcceptAll: () => void;
  onSuggest: () => void;
};

const Limitations: FC<{ text: string | null | undefined }> = ({ text }) =>
  text ? (
    <p className="text-sm text-fg-3" data-testid="suggestion-limitations">
      Could not verify: {text}
    </p>
  ) : null;

export const SuggestionStatusRow: FC<Props> = ({
  suggestion,
  pendingCount,
  busy,
  changes,
  onAcceptAll,
  onSuggest
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  if (!suggestion) {
    return (
      <div
        className="flex items-center justify-between gap-3 text-base text-fg-3"
        data-testid="suggestion-status"
        data-status="none"
      >
        <span className="inline-flex items-center gap-2">
          <Sparkles className="size-3.5" />
          The agent hasn't read this document yet.
        </span>
        <Button
          variant="secondary"
          size="sm"
          disabled={busy}
          onClick={onSuggest}
        >
          <Sparkles />
          Suggest From the Document
        </Button>
      </div>
    );
  }

  if (suggestion.status === "pending") {
    return (
      <div
        className="flex items-center gap-2 text-base text-fg-3"
        data-testid="suggestion-status"
        data-status="pending"
      >
        <Loader2 className="size-3.5 animate-spin" />
        Reading the document…
      </div>
    );
  }

  if (suggestion.status !== "ready") return null;
  if (pendingCount === 0) return <Limitations text={suggestion.limitations} />;

  return (
    <div
      className="space-y-1"
      data-testid="suggestion-status"
      data-status="ready"
      data-pending={pendingCount}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="inline-flex min-w-0 items-center gap-2 text-base">
          <Sparkles className="size-3.5 shrink-0 text-accent-seed" />
          <span className="truncate">
            The agent read it differently · {pendingCount}{" "}
            {pendingCount === 1 ? "suggestion" : "suggestions"}
          </span>
        </span>
        <Popover open={previewOpen} onOpenChange={setPreviewOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              data-testid="accept-all-suggestions"
            >
              <Check />
              Accept All
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            className="w-80 space-y-2 p-3"
            data-testid="accept-all-preview"
          >
            <p className="text-sm font-medium">
              This fills in the form; nothing is saved until you Save.
            </p>
            <ul className="space-y-1.5 text-sm">
              {changes.map((change) => (
                <li key={change.field} className="flex gap-2">
                  <span className="w-20 shrink-0 text-fg-3">
                    {change.label}
                  </span>
                  <span className="line-clamp-2 min-w-0 break-words">
                    {change.value}
                  </span>
                </li>
              ))}
            </ul>
            <div className="flex justify-end gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPreviewOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setPreviewOpen(false);
                  onAcceptAll();
                }}
                data-testid="confirm-accept-all"
              >
                {changes.length === 1 ? "Accept This" : "Accept These"}
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <Limitations text={suggestion.limitations} />
    </div>
  );
};
